'use client';
import { faClose, faFilterList } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'next/navigation';
import { FC, useEffect, useState } from 'react';
import { Badge, Button, Chip, Icon, Tooltip } from '@/components/ui';
import useProductListingSearchParams from '@/hooks/use-product-listing-search-params';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useTooltip } from '@/providers/tooltip-provider';
import {
  clearProductListingFilterParams,
  getProductListingFilterEntries,
} from '@/utils/product-listing-search-params';

type ActiveFilter = {
  key: string;
  label: string;
  value: string;
};

const ActiveFilters: FC = () => {
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>() ?? {};
  const router = useRouter();
  const searchParams = useProductListingSearchParams();
  const listingSearchParamsOptions = { isStudioPreview: Boolean(isStudioPreview) };

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const { setIsTooltipOpen } = useTooltip();

  useEffect(() => {
    const filters: ActiveFilter[] = getProductListingFilterEntries(searchParams, {
      isStudioPreview: Boolean(isStudioPreview),
    }).map(([key, value]) => ({
      key,
      label:
        key === 'price' ? (t?.common.price as string) : key.charAt(0).toUpperCase() + key.slice(1),
      value,
    }));
    setActiveFilters(filters);
  }, [isStudioPreview, searchParams, t?.common.price]);

  const handleRemoveFilter = (key: string, value: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    const values = newSearchParams.getAll(key).filter(val => val !== value);
    newSearchParams.delete(key);
    values.forEach(val => newSearchParams.append(key, val));
    router.push(`?${newSearchParams.toString()}`);
  };

  const handleClearAll = () => {
    const newSearchParams = clearProductListingFilterParams(
      searchParams,
      listingSearchParamsOptions,
    );
    router.push(`?${newSearchParams.toString()}`);
  };

  const groupedFilters = activeFilters.reduce(
    (acc, filter) => {
      if (!acc[filter.key]) {
        acc[filter.key] = { label: filter.label, values: [] };
      }
      acc[filter.key].values.push(filter.value);
      return acc;
    },
    {} as Record<string, { label: string; values: string[] }>,
  );

  const totalValuesCount = Object.values(groupedFilters).reduce(
    (sum, group) => sum + group.values.length,
    0,
  );

  useEffect(() => {
    if (totalValuesCount === 0) {
      setIsTooltipOpen(false);
    }
  }, [setIsTooltipOpen, totalValuesCount]);

  if (totalValuesCount === 0) {
    return null;
  }

  return (
    <Badge content={totalValuesCount} data-testid="active-filters__badge">
      <Tooltip
        placement="bottom"
        className="overflow-hidden"
        classNames={{ content: 'p-0' }}
        onOpenChange={open => setIsTooltipOpen(open)}
        showArrow
        content={
          <div
            className="flex max-h-96 w-[400px] flex-col overflow-hidden p-4"
            data-testid="active-filters__tooltip"
          >
            <div
              className="flex scrollbar-thin flex-col overflow-y-auto"
              data-testid="active-filters__filters-container"
            >
              {Object.entries(groupedFilters).map(([key, { label, values }], index) => (
                <div key={key || index} className="mb-4">
                  <div
                    className="mb-2 font-bold text-black"
                    data-testid={`active-filters__filter-label-${label}`}
                  >
                    {label}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {values.map((value, idx) => (
                      <Chip
                        key={idx}
                        className="h-10 flex-shrink-0 rounded-full border bg-white text-gray-500"
                        endContent={
                          <Icon
                            icon={faClose}
                            className="ml-2 cursor-pointer rounded-full p-2 hover:bg-gray-200"
                            data-testid={`active-filters__remove-${label}-${value}`}
                            onClick={() => handleRemoveFilter(key, value)}
                          />
                        }
                      >
                        {value}
                      </Chip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full border-t">
              <Button
                fullWidth
                className="mt-4"
                variant="flat"
                onClick={handleClearAll}
                data-testid="active-filters__clear-all"
              >
                {t?.products.deleteAllFilters}
              </Button>
            </div>
          </div>
        }
      >
        <Button
          variant="flat"
          color="primary"
          startContent={<Icon icon={faFilterList} />}
          data-testid="active-filters__button"
        >
          {t?.products.filters}
        </Button>
      </Tooltip>
    </Badge>
  );
};

export default ActiveFilters;
